import { Injectable } from '@angular/core';
import { Device, DeviceId, DeviceInfo } from '@capacitor/device';
import {
  PushNotifications,
  Token,
  PermissionStatus,
  RegistrationError,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { Router } from '@angular/router';
import { App, AppInfo } from '@capacitor/app';
import { FCM } from '@capacitor-community/fcm';

import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService } from '#services/intermediary/intermediary.service';
import { logger } from '#lib/logger';
import { AuthService } from '#services/auth/auth.service';
import { ToastButton } from '@ionic/angular';
import { ToastService } from '#services/toast/toast.service';
import { Capacitor } from '@capacitor/core';
import {
  PushNotificationDeviceInfoRequest,
  PushNotificationDeviceInfoResponse,
} from '#models/db/push-notification/push-notification.model';
import { ResponseBase } from '#models/db/response/response-base.model';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  path = 'notifications';

  /**
   * Path to navigate when the user taps on the notification. Only need to capture this if the user is not logged in.
   * Once the user logs in, the path will be navigated to.
   *
   * This can only be set by the `pushNotificationActionPerformed` listener. It will be unset after navigating to the path.
   */
  tapActionPerformedPath: string | null = null;
  pushToken: string | null = null;

  private _deviceId: DeviceId | null = null;
  private _deviceInfo: DeviceInfo | null = null;
  private _appInfo: AppInfo | null = null;

  get deviceId(): DeviceId | null {
    return this._deviceId;
  }

  get deviceInfo(): DeviceInfo | null {
    return this._deviceInfo;
  }

  get appInfo(): AppInfo | null {
    return this._appInfo;
  }

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService,
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService
  ) {}

  async init(): Promise<void> {
    await this.getDeviceId();
    await this.getDeviceInfo();

    if (Capacitor.getPlatform() !== 'web') {
      await this.getAppInfo();
      await this.addListeners();
    }
  }

  /**
   * Registers the application for push notifications.
   *
   * This method checks the current push notification permission status.
   * If the status is 'prompt', it requests the necessary permissions from the user.
   * If the permission is granted, it proceeds to register the device for push notifications.
   * If the permission is not granted, the registration process is aborted.
   *
   * @returns {Promise<void>} A promise that resolves when the registration process is complete.
   */
  async registerNotifications(): Promise<void> {
    if (Capacitor.getPlatform() !== 'web') {
      let status: PermissionStatus = await PushNotifications.checkPermissions();

      logger.log(status.receive, 'PushNotificationService.registerNotifications');

      if (status.receive === 'prompt') {
        status = await PushNotifications.requestPermissions();
      }

      if (status.receive !== 'granted') {
        return;
      }

      await PushNotifications.register();
    }
  }

  /**
   * Unregisters all push notification event listeners.
   *
   * This method removes all listeners that have been registered for push notification events,
   * effectively disabling any further push notification handling until new listeners are added.
   *
   * @returns A promise that resolves when all listeners have been removed.
   */
  async unregisterNotifications(): Promise<void> {
    await PushNotifications.removeAllListeners();
  }

  /**
   * Retrieves the device ID using Capacitor's Device API and sets it to the `_deviceId` property. This is used to uniquely identify the device for push notifications.
   *
   * @returns {Promise<void>} A promise that resolves when the device ID has been retrieved and set.
   */
  async getDeviceId(): Promise<void> {
    this._deviceId = await Device.getId();
    logger.log(this._deviceId, 'PushNotificationService.getDeviceId');
  }

  /**
   * Retrieves the device information using Capacitor's Device API and sets it to the `_deviceInfo` property. This includes details like the device model, operating system, and more.
   *
   * @returns {Promise<void>} A promise that resolves when the device information has been retrieved and stored.
   */
  async getDeviceInfo(): Promise<void> {
    this._deviceInfo = await Device.getInfo();
    logger.log(this._deviceInfo, 'PushNotificationService.getDeviceInfo');
  }

  async getAppInfo(): Promise<void> {
    this._appInfo = await App.getInfo();
    logger.log(this._appInfo, 'PushNotificationService.getAppInfo');
  }

  /**
   * Resets the `tapActionPerformedPath` property to `null`.
   * This method is typically used to clear any previously set path
   * that was triggered by a tap action notification.
   *
   * Sets the following:
   * * {@link tapActionPerformedPath}
   */
  unsetTapActionPerformedPath(): void {
    this.tapActionPerformedPath = null;
  }

  private async addListeners(): Promise<void> {
    // Remove all existing listeners to avoid duplicates.
    await this.unregisterNotifications();
    await this.registrationListener();
    await this.pushNotificationReceivedListener();
    await this.pushNotificationActionPerformedListener();
  }

  /**
   * Sets up listeners for push notification registration events.
   *
   * This method registers two listeners:
   * - 'registration': Triggered when the device successfully registers for push notifications.
   * - 'registrationError': Triggered when there is an error during registration.
   *
   * @returns {Promise<void>} A promise that resolves when the listeners are set up.
   */
  private async registrationListener(): Promise<void> {
    await PushNotifications.addListener('registration', async (token: Token) => {
      const fcmToken = await FCM.getToken();

      logger.log(
        token.value,
        'PushNotificationService.registrationListener.registration -> APN token'
      );
      logger.log(
        fcmToken.token,
        'PushNotificationService.registrationListener.registration -> fcmToken'
      );

      this.pushToken = fcmToken.token;

      const access = await this.authService.hasValidAccessToken();

      if (access) {
        this.setPushNotificationDeviceInfo({
          device_id: this.deviceId?.identifier,
          platform: this.deviceInfo?.platform,
          device_name: this.deviceInfo?.name || '',
          device_model: this.deviceInfo?.model || '',
          device_os: this.deviceInfo?.operatingSystem || '',
          app_version: this._appInfo?.version || '',
          fcm_token: this.pushToken || '',
        });
      }
    });

    await PushNotifications.addListener('registrationError', (error: RegistrationError) => {
      logger.log(error, 'PushNotificationService.registrationListener.registrationError -> error');
    });
  }

  /**
   * Sets up a listener for the 'pushNotificationReceived' event using the PushNotifications plugin.
   *
   * When a push notification is received:
   * - Logs the notification details for debugging purposes.
   * - On Android devices, displays a toast notification with the notification's title and body.
   *   - If the notification contains a deep link, shows a button to navigate to the specified path.
   *   - Otherwise, shows a close button to dismiss the toast.
   * - On iOS devices, relies on the system to handle the notification in the background.
   *
   * @returns {Promise<void>} A promise that resolves when the listener is registered.
   */
  private async pushNotificationReceivedListener(): Promise<void> {
    await PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        logger.log(
          notification,
          'PushNotificationService.registrationListener.pushNotificationReceived -> notification'
        );

        // iOS will handle the notification in the background.
        if (Capacitor.getPlatform() === 'android') {
          const path = this.getDeepLinkUrl(notification.data);

          // Default to close button.
          let buttons: ToastButton[] = [
            {
              icon: 'close',
              role: 'cancel',
              side: 'end',
            },
          ];

          // Handling a deep link, if applicable.
          if (path) {
            buttons = [
              {
                icon: 'chevron-forward-outline',
                side: 'end',
                handler: () => {
                  this.router.navigateByUrl(path);
                },
              },
            ];
          }

          await this.toastService.showToast({
            header: notification.title,
            message: notification.body || '',
            duration: 5000,
            position: 'top',
            color: 'warning',
            buttons,
          });
        }
      }
    );
  }

  /**
   * Sets up a listener for the 'pushNotificationActionPerformed' event.
   *
   * When a push notification action is performed, this method logs the notification,
   * extracts a deep link URL from the notification data, and checks if the user has a valid access token.
   * If a valid path is found and the user is authenticated, it navigates to the specified path and unsets any stored tap action path.
   * If the user is not authenticated, it stores the path for later navigation after authentication.
   *
   * @private
   * @returns {Promise<void>} A promise that resolves when the listener is set up.
   */
  private async pushNotificationActionPerformedListener(): Promise<void> {
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        logger.log(
          notification,
          'PushNotificationService.registrationListener.pushNotificationActionPerformed -> notification'
        );

        const path = this.getDeepLinkUrl(notification.notification.data);
        const access = await this.authService.hasValidAccessToken();

        // Check to make sure the path is not undefined.
        if (path) {
          if (access) {
            await this.router.navigateByUrl(path);
            this.unsetTapActionPerformedPath();
          } else {
            this.tapActionPerformedPath = path;
          }
        }
      }
    );
  }

  /**
   * Extracts the deep link path from a given data object containing a URL.
   *
   * @param data - An unknown object that may contain a `url` property.
   * @returns The extracted deep link path as a string, or an empty string if not found.
   */
  private getDeepLinkUrl(data: unknown): string {
    let path = '';

    if (typeof data === 'object' && data !== null && 'url' in data) {
      // Type assertion to access url property safely
      const url = (data as { url: string }).url;

      path = url.split('://app').pop()!;
    }

    return path;
  }

  /**
   * Sends the device information required for push notifications to the server.
   *
   * @param payload - The device information payload to be sent to the server.
   * @param nextFn - Optional callback function to handle the response data.
   */
  setPushNotificationDeviceInfo(
    payload: PushNotificationDeviceInfoRequest,
    nextFn?: (data: PushNotificationDeviceInfoResponse | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<
          ResponseBase<PushNotificationDeviceInfoResponse>,
          PushNotificationDeviceInfoRequest
        >(
          {
            url: `${this.path}/register-device`,
          },
          payload
        );

        logger.log(response, 'PushNotificationService.setPushNotificationDeviceInfo -> response');

        return response.details;
      }
    );
  }
}
